import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, API_BASE } from '~/lib/api';

const ERROR_MESSAGES: Record<string, string> = {
  missing_gmail_scope: 'No se otorgaron los permisos de Gmail necesarios. Intenta de nuevo y acepta todos los permisos.',
  auth_failed: 'Hubo un error durante la autenticacion. Intenta de nuevo.',
  no_code: 'No se recibio el codigo de autorizacion. Intenta de nuevo.',
};

export default function JobApplyConnectPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const success = params.get('success') === 'true';
  const error = params.get('error');

  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState('');

  const activateManagedSending = async () => {
    setActivating(true);
    setActivationError('');
    try {
      const res = await apiFetch('/job-apply/api/onboarding/email-channel', { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setActivationError(body?.error || 'No pudimos activar tu cuenta. Intenta de nuevo.');
        return;
      }
      navigate('/job-apply/onboarding', { replace: true });
    } catch {
      setActivationError('Error de conexion. Intenta de nuevo.');
    } finally {
      setActivating(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center gap-6 py-12">
        <div className="card bg-base-100 shadow-lg max-w-md w-full">
          <div className="card-body items-center text-center">
            <div className="text-success text-5xl mb-2">&#10003;</div>
            <h2 className="card-title text-2xl">Cuenta conectada</h2>
            <p className="text-base-content/70">
              Tu cuenta de Google ha sido vinculada exitosamente.
            </p>
            <div className="divider" />
            <p className="text-base-content/80">
              El servicio de postulacion automatica inicia el{' '}
              <span className="font-semibold">miercoles 11 de marzo de 2026</span>.
              Te notificaremos por correo cuando este activo.
            </p>
            <p className="text-sm text-base-content/50 mt-2">
              No necesitas hacer nada mas por ahora.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card bg-base-100 shadow-lg max-w-md w-full">
        <div className="card-body items-center text-center">
          <h2 className="card-title text-2xl">Empieza a postular hoy</h2>
          <p className="text-base-content/70">
            Postulamos por ti a las ofertas con visa patrocinada. Nosotros ponemos el
            correo desde el que salen tus postulaciones y las respuestas de las
            empresas te llegan a tu correo de siempre.
          </p>

          {(error || activationError) && (
            <div className="alert alert-error text-sm mt-2">
              {activationError || ERROR_MESSAGES[error!] || `Error: ${error}`}
            </div>
          )}

          <div className="card-actions mt-4 w-full flex-col gap-3">
            <button
              className="btn btn-primary w-full"
              onClick={activateManagedSending}
              disabled={activating}
            >
              {activating && <span className="loading loading-spinner loading-sm" />}
              Continuar con envio gestionado por TBit
            </button>
            <p className="text-xs text-base-content/50">
              Sin conectar ninguna cuenta. Tardas menos de un minuto.
            </p>

            <div className="divider text-xs text-base-content/40 my-1">o</div>

            <a href={`${API_BASE}/job-apply/auth/start`} className="btn btn-outline w-full gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Prefiero postular desde mi Gmail
            </a>
            <p className="text-xs text-base-content/50">
              Tus postulaciones salen de tu propio correo. Tus credenciales estan
              encriptadas y nunca se comparten.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
